import React, { useState } from "react";
import { Form, Input, Checkbox, Button } from "antd";
import isEmail from "validator/lib/isEmail";

import ReCAPTCHA from "react-google-recaptcha";

import { RECAPTCHA_PUBLIC_KEY } from "../../../utils/constants";
import { checkEmailValidity } from "../../../api/asyncValidation";
import { updateSignupParams } from "../../../actions/signupActions";
import { navigate } from "@reach/router";
import { steps } from "../../../utils/steps";
import { useSignupContext } from "../../../context/SignupContext";

export const companyValidator = (rule, value, callback) => {
    const re = /[^a-zA-Z0-9]+/g;
    if (!value) {
        callback("Please input your company name");
        return;
    } else if (value.replace(re, "").length < 3) {
        callback("Must have at least 3 letters and/or numbers");
        return;
    }
    callback();
};

const RegistrationForm = props => {
    const {
        state: { errors },
        dispatch: signupDispatch,
    } = useSignupContext();
    const { signupParams } = props;
    const { getFieldDecorator, isFieldValidating } = props.form;
    const [checkEmailLoading, setCheckEmailLoading] = useState(false);

    const handleSubmit = e => {
        e.preventDefault();
        props.form.validateFieldsAndScroll((err, values) => {
            if (!err) {
                signupDispatch(updateSignupParams({ ...values }));
                navigate(steps[2].url); //proceed if signup success
            }
        });
    };

    // if navigating back from the Billing page, we want to clear errors from that page
    if (errors !== null) {
        errors.signup = null;
    }

    //validate email field
    const checkEmail = (rule, value, callback) => {
        if (!value) {
            //check if the value is null
            callback("Please input your email address.");
        } else if (value.indexOf("+") > 1 && process.env.NODE_ENV !== "development") {
            //only check '+' rule for production
            callback("Email addresses with the '+' character are not supported.");
        } else if (isEmail(value)) {
            setCheckEmailLoading(true);
            //if the value is valid email format
            checkEmailValidity(value) //async check if the email is available
                .then(response => {
                    callback(); //email check passed
                })
                .catch(error => {
                    if (error.response.status === 400) {
                        callback("Please enter a valid email address.");
                    } else if (error.response.status === 403) {
                        callback("Emails from this domain are not allowed for sign-up. Please use a different email address.");
                    } else if (error.response.status === 409) {
                        callback("This email address is already in use.");
                    } else {
                        callback("This email is not available.");
                    }
                })
                .then(() => setCheckEmailLoading(false));
        } else {
            //the value is not valid email format
            callback("Please enter a valid email address.");
        }
        return;
    };

    //validate accepted terms field
    const checkTermsValidator = (rule, value, callback) => {
        if (!value) {
            //if the checkbox is unchekced
            callback("Please read and accept the Terms of Service before proceeding");
        }
        callback();
    };

    const passwordValidator = (rule, value, callback) => {
        if (!value) {
            //check if the value is null
            callback("Please input your password");
        } else if (value.length < 6) {
            callback("Must have at least 6 characters");
        } else if ((new TextEncoder().encode(value).length) > 72) {
            callback("Must have at most 72 characters. Some multi-byte characters (like emojis) may count as more than one character");
        } else if (!/\p{N}/u.test(value)) {
            callback("Must have at least 1 number");
        } else if (!/\p{Lu}/u.test(value)) {
            callback("Must have at least 1 uppercase letter");
        } else if (!/\p{Ll}/u.test(value)) {
            callback("Must have at least 1 lowercase letter");
        }  else if (!/[^\p{L}\p{N}]/u.test(value)) {
            callback("Must have at least 1 special character");
        }
        callback();
    };

    const formItemLayout = {
        labelCol: {
            xs: { span: 24 },
        },
        wrapperCol: {
            xs: { span: 24 },
        },
    };

    return (
        <>
            <Form onSubmit={e => handleSubmit(e)} style={{ textAlign: "left" }}>
                <Form.Item
                    {...formItemLayout}
                    label="Email"
                    hasFeedback={isFieldValidating("email") !== undefined} //show input feedback for email status (only after validation)
                    {...checkEmailLoading && { validateStatus: "validating" }} //show spinner when async validation is in process
                >
                    {getFieldDecorator("email", {
                        validateTrigger: "onBlur", //we don't want to trigger async validation on every user type-in stroke, so only validate after user finished the input
                        rules: [
                            {
                                required: true,
                                validator: checkEmail,
                            },
                        ],
                        initialValue: signupParams.email,
                    })(<Input />)}
                </Form.Item>
                <Form.Item
                    {...formItemLayout}
                    label="Password"
                    hasFeedback={isFieldValidating("password") !== undefined}
                >
                    {getFieldDecorator("password", {
                        rules: [
                            {
                                required: true,
                                validator: passwordValidator,
                            },
                        ],
                        initialValue: signupParams.password,
                    })(<Input.Password />)}
                </Form.Item>
                <Form.Item
                    {...formItemLayout}
                    label="Company Name"
                    hasFeedback={isFieldValidating("companyName") !== undefined}
                >
                    {getFieldDecorator("companyName", {
                        rules: [
                            {
                                required: true,
                                validator: companyValidator,
                            },
                        ],
                        initialValue: signupParams.companyName,
                    })(<Input />)}
                </Form.Item>
                <div
                    style={{
                        color: "grey",
                        paddingBottom: "0.5rem",
                    }}
                >
                    {`*For existing customers signing up for a new product, please use a different email address.`}
                </div>
                <Form.Item {...formItemLayout} style={{ textAlign: "center" }}>
                    {getFieldDecorator("acceptedTerms", {
                        valuePropName: "checked",
                        rules: [
                            {
                                required: true,
                                validator: checkTermsValidator,
                                message:
                                    "Please read and accept the Terms of Service before proceeding",
                            },
                        ],
                        initialValue: signupParams.acceptedTerms,
                    })(
                        <Checkbox style={{ lineHeight: "1.5rem"}}>
                            I have read and accepted the
                            <a
                                href="https://www.mailchannels.com/terms-of-service/"
                                rel="noopener noreferrer"
                                target="_blank"
                            >
                                {" "}
                                Terms of Service{" "}
                            </a>
                            and
                            <a
                                href="https://www.mailchannels.com/privacy-policy/"
                                rel="noopener noreferrer"
                                target="_blank"
                            >
                                {" "}
                                Privacy Policy
                            </a>
                        </Checkbox>
                    )}
                </Form.Item>
                <div style={{ textAlign: "center" }}>
                    <Button type="primary" size="large" htmlType="submit">
                        {`Continue`}
                    </Button>
                </div>
            </Form>
            <div className="reCaptchaWrapper">
                <ReCAPTCHA sitekey={RECAPTCHA_PUBLIC_KEY} size="invisible" />
            </div>
        </>
    );
};

const WrappedRegistrationForm = Form.create({
    name: "register",
    onFieldsChange(props, changedFields) {
        // update the changed signupParams to context store
        let changedParams = {};
        Object.keys(changedFields).forEach(fieldName => {
            changedParams[fieldName] = changedFields[fieldName].value;
        });
        const { onFormsChange } = props;
        onFormsChange(changedParams);
    },
})(RegistrationForm);

export default WrappedRegistrationForm;
